import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Chart } from 'chart.js';
import { AnalyticsService } from '../../../../services/analytics.service';
import { Subscription } from 'rxjs';
import { formatMinutes, getBarChartConfig, PARKBOT_COLORS } from '../../../utils/chart-config';
import { Router } from '@angular/router';

@Component({
  selector: 'app-card-average-duration',
  templateUrl: './card-average-duration.component.html'
})
export class CardAverageDurationComponent implements OnInit, AfterViewInit, OnDestroy {
  durationData: any;
  chart: Chart;
  subscriptions: Subscription[] = [];
  vehicleTypes: any[] = [];
  account: any;
  colorMap: Map<string, string> = new Map();
  isLoading = false;
  
  constructor(
    private analyticsService: AnalyticsService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Subscribe to loading state
    this.subscriptions.push(
      this.analyticsService.loading$.subscribe(loading => {
        this.isLoading = loading;
      })
    );

    this.account = JSON.parse(localStorage.getItem('account'));
    
    // Set up predefined vehicle types based on the dynamic structure
    this.vehicleTypes = [
      { type: 'light_vehicle', name: 'Car', image: 'assets/img/light-vehicle.png' },
      { type: 'motorcycle', name: 'Motorcycle', image: 'assets/img/motorcycle.png' },
      { type: 'valet_light_vehicle', name: 'Valet (Guest)', image: 'assets/img/light-vehicle.png' },
      { type: 'vip_light_vehicle', name: 'Doctor (Paid)', image: 'assets/img/light-vehicle.png' },
      { type: 'vip_free_light_vehicle', name: 'Doctor (Free)', image: 'assets/img/light-vehicle.png' }
    ];
    
    // Create consistent color mapping for vehicle types
    this.assignColorsToVehicleTypes();
    
    // Listen for date change events from analytics service
    this.subscriptions.push(
      this.analyticsService.dateRangeChanged.subscribe(() => {
        this.loadDurationData();
      })
    );
    
    // Initial load
    this.loadDurationData();
  }
  

  
  private assignColorsToVehicleTypes(): void {
    // Assign consistent colors to vehicle types matching other components
    this.colorMap.clear();
    const colorMapping = {
      'light_vehicle': '#3B82F6',
      'motorcycle': '#EF4444', 
      'valet_light_vehicle': '#10B981',
      'vip_light_vehicle': '#F59E0B',
      'vip_free_light_vehicle': '#8B5CF6'
    };
    
    this.vehicleTypes.forEach(vt => {
      if (vt && vt.type) {
        this.colorMap.set(vt.type, colorMapping[vt.type] || PARKBOT_COLORS.primary);
      }
    });
  }

  loadDurationData(): void {
    this.subscriptions.push(
      this.analyticsService.getAverageParkingDuration()
        .subscribe(data => {
          this.durationData = data;
          
          // Initialize any missing vehicle types with 0 in the combined categories
          if (this.vehicleTypes && this.vehicleTypes.length > 0) {
            this.vehicleTypes.forEach(vt => {
              if (!this.durationData.averageByCombined[vt.type]) {
                this.durationData.averageByCombined[vt.type] = {
                  averageMinutes: 0,
                  count: 0
                };
              }
            });
          }
          
          if (this.chart) {
            this.updateChart();
          } else {
            this.initChart();
          }
        })
    );
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initChart();
    }, 100);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => {
      if (sub) sub.unsubscribe();
    });
    
    if (this.chart) {
      this.chart.destroy();
    }
  }

  formatMinutes(minutes: number): string {
    return formatMinutes(minutes);
  }

  private initChart(): void {
    if (!this.durationData) return;
    
    const canvas = document.getElementById('duration-chart') as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Get average duration by combined categories for chart
    let vehicleTypeData = {};
    
    if (this.vehicleTypes && this.vehicleTypes.length > 0) {
      // Use configured vehicle types with combined categories
      this.vehicleTypes.forEach(vt => {
        const categoryData = this.durationData.averageByCombined[vt.type];
        if (categoryData && categoryData.averageMinutes > 0) {
          vehicleTypeData[vt.name] = categoryData.averageMinutes;
        }
      });
    }
    
    // Filter out vehicle types with no data
    const filteredVehicleTypes = Object.entries(vehicleTypeData)
      .filter(([_, value]) => (value as number) > 0)
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
    
    const labels = Object.keys(filteredVehicleTypes);
    const data = Object.values(filteredVehicleTypes);
    
    // If no vehicle type data, show zone-based data as fallback
    if (labels.length === 0) {
      // Prepare data for zone-based durations
      const zoneData = this.durationData.averageByZone || {};
      const zones = Object.keys(zoneData).slice(0, 10); // Limit to 10 zones for readability
      
      if (zones.length > 0) {
        const zoneLabels = zones.map(zoneId => zoneData[zoneId].zoneName || `Zone ${zoneId}`);
        const zoneDataValues = zones.map(zoneId => zoneData[zoneId].averageMinutes);
        
        const datasets = [
          {
            label: 'Average Duration (minutes)',
            backgroundColor: PARKBOT_COLORS.primary,
            borderColor: PARKBOT_COLORS.primary,
            data: zoneDataValues,
            barPercentage: 0.6,
            categoryPercentage: 0.7
          }
        ];

        const config = getBarChartConfig(zoneLabels, datasets);
        
        // Add custom tooltip to format duration
        config.options.plugins.tooltip = {
          ...config.options.plugins.tooltip,
          callbacks: {
            label: (context) => {
              const label = context.dataset.label || '';
              const value = context.raw || 0;
              return `${label}: ${formatMinutes(value as number)}`;
            }
          }
        } as any;
        
        // Use horizontal bar for better zone name display
        (config.options as any).indexAxis = 'y';
        
        this.chart = new Chart(ctx, config);
        return;
      } else {
        // No data at all, show empty chart
        const datasets = [
          {
            label: 'Average Duration (minutes)',
            backgroundColor: PARKBOT_COLORS.primary,
            borderColor: PARKBOT_COLORS.primary,
            data: [],
            barPercentage: 0.6,
            categoryPercentage: 0.7
          }
        ];

        const config = getBarChartConfig([], datasets);
        this.chart = new Chart(ctx, config);
        return;
      }
    }
    
    // Otherwise, show vehicle type data with consistent colors
    const colors = labels.map(label => {
      // Find the vehicle type object that matches this label
      const vehicleType = this.vehicleTypes.find(vt => vt.name === label);
      if (vehicleType && this.colorMap.has(vehicleType.type)) {
        return this.colorMap.get(vehicleType.type);
      }
      // Fallback to a default color
      return PARKBOT_COLORS.primary;
    });
    
    const datasets = [
      {
        label: 'Average Duration (minutes)',
        backgroundColor: colors,
        borderColor: colors,
        data: data,
        barPercentage: 0.6,
        categoryPercentage: 0.7
      }
    ];

    const config = getBarChartConfig(labels, datasets);
    
    // Add custom tooltip to format duration
    config.options.plugins.tooltip = {
      ...config.options.plugins.tooltip,
      callbacks: {
        label: (context) => {
          const label = context.dataset.label || '';
          const value = context.raw || 0;
          return `${label}: ${formatMinutes(value as number)}`;
        }
      }
    } as any;
    
    this.chart = new Chart(ctx, config);
  }

  private updateChart(): void {
    if (!this.chart || !this.durationData) return;
    
    // Try to update with vehicle type data using combined categories
    let vehicleTypeData = {};
    
    if (this.vehicleTypes && this.vehicleTypes.length > 0) {
      // Use configured vehicle types with combined categories
      this.vehicleTypes.forEach(vt => {
        const categoryData = this.durationData.averageByCombined[vt.type];
        if (categoryData && categoryData.averageMinutes > 0) {
          vehicleTypeData[vt.name] = categoryData.averageMinutes;
        }
      });
    }
    
    // Filter out vehicle types with no data
    const filteredTypes = Object.entries(vehicleTypeData)
      .filter(([_, value]) => (value as number) > 0)
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
    
    if (Object.keys(filteredTypes).length > 0) {
      // Update with vehicle type data
      const labels = Object.keys(filteredTypes);
      const data = Object.values(filteredTypes);
      
      // Update colors to be consistent with the color map
      const colors = labels.map(label => {
        // Find the vehicle type object that matches this label
        const vehicleType = this.vehicleTypes.find(vt => vt.name === label);
        if (vehicleType && this.colorMap.has(vehicleType.type)) {
          return this.colorMap.get(vehicleType.type);
        }
        // Fallback to a default color
        return PARKBOT_COLORS.primary;
      });
      
      this.chart.data.labels = labels;
      this.chart.data.datasets[0].data = data;
      this.chart.data.datasets[0].backgroundColor = colors;
      this.chart.data.datasets[0].borderColor = colors;
    } else {
      // Fallback to zone-based data
      const zoneData = this.durationData.averageByZone || {};
      const zones = Object.keys(zoneData).slice(0, 10);
      
      if (zones.length > 0) {
        this.chart.data.labels = zones.map(zoneId => zoneData[zoneId].zoneName || `Zone ${zoneId}`);
        this.chart.data.datasets[0].data = zones.map(zoneId => zoneData[zoneId].averageMinutes);
        
        // Reset colors for zone data
        const color = PARKBOT_COLORS.primary;
        this.chart.data.datasets[0].backgroundColor = Array(zones.length).fill(color);
        this.chart.data.datasets[0].borderColor = Array(zones.length).fill(color);
      } else {
        // No data at all
        this.chart.data.labels = [];
        this.chart.data.datasets[0].data = [];
        this.chart.data.datasets[0].backgroundColor = [];
        this.chart.data.datasets[0].borderColor = [];
      }
    }
    
    this.chart.update();
  }
}
